import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import CargomaticSelect from 'components/v2/select';
import CustomerSearch from 'components/site/customer-search-alt';
import _ from 'lodash';
import { titleize, humanize } from 'underscore.string';
import { equipmentTypesMapping, assetTypes, equipmentTypes, equipmentSizes, CHASSIS_OWNERSHIP_TYPE, } from 'constants/Equipment';
import { getCustomerName } from 'helpers/users';
const DISABLED_ORDER_STATUSES = ['created', 'en_route'];
const disableStatusDropdown = (equipment) => {
    const { order, _equipmentType } = equipment;
    if (_equipmentType && _equipmentType === 'container') {
        if (order) {
            const { order_type, status } = order;
            if (order_type === 'import' && DISABLED_ORDER_STATUSES.includes(status)) {
                return true;
            }
        }
    }
    return false;
};
const emptyTooltip = React.createElement(Tooltip, { id: "empty", bsClass: "invisible" });
const EXPORT_DATES = [
    'earliest_empty_date',
    'earliest_return_date',
    'last_cut_date',
];
const IMPORT_DATES = ['last_free_date'];
class EquipmentFormRow extends Component {
    constructor() {
        super(...arguments);
        this.disableDatePicker = (id) => {
            const { equipment } = this.props;
            if (equipment && equipment.order) {
                const { order: { order_type }, } = equipment;
                if (order_type === 'import') {
                    return !IMPORT_DATES.includes(id);
                }
                return !EXPORT_DATES.includes(id);
            }
            return true;
        };
        this.handleApplyToAll = () => this.props.onApplyToAll(this.props.equipment._id);
        this.handleChange = (field) => (e) => {
            const value = e.target.value;
            this.props.onFieldChange(this.props.equipment._id, field, value);
        };
        this.handleToggleChange = (field) => (value) => {
            this.props.onFieldChange(this.props.equipment._id, field, value);
        };
        this.handleDateChange = (field) => (date) => this.props.onFieldChange(this.props.equipment._id, field, date);
        this.handleDeselect = () => this.props.onDeselect(this.props.equipment._id);
    }
    getValidationClassName(field) {
        const error = this.props.equipment.error || {};
        if (error[field]) {
            return {
                error: true,
            };
        }
        return {
            error: false,
        };
    }
    disableStatusTooltip() {
        const { t } = this.props;
        return (React.createElement(Tooltip, { id: "equipment_status" }, t('DISABLED_EQUIPMENT_TOOLTIP')));
    }
    renderEquipmentIdField() {
        const className = Object.assign({}, this.getValidationClassName('equipment_id'));
        const { equipment } = this.props;
        return (React.createElement("div", { className: classnames(className) },
            React.createElement("input", { type: "text", className: "form-field w-full", value: equipment.equipment_id, onChange: this.handleChange('equipment_id'), placeholder: "Enter Equipment Number" })));
    }
    renderAssetTypeField() {
        const { equipment, t } = this.props;
        return (React.createElement("div", { className: classnames(this.getValidationClassName('_equipmentType')) },
            React.createElement("select", { onChange: this.handleChange('_equipmentType'), value: equipment._equipmentType, className: "form-select mb-4 w-full" },
                React.createElement("option", { value: "" }, t('SELECT_ASSET_TYPE')),
                assetTypes.map((assetType) => (React.createElement("option", { key: assetType, value: assetType }, t(assetType.toUpperCase())))))));
    }
    renderStatuses() {
        const { equipment, t } = this.props;
        return (React.createElement("div", { className: classnames(this.getValidationClassName('status')) },
            React.createElement(OverlayTrigger, { id: `${equipment._id}_status`, placement: "top", overlay: disableStatusDropdown(equipment)
                    ? this.disableStatusTooltip
                    : emptyTooltip },
                React.createElement("span", null,
                    React.createElement("select", { onChange: this.handleChange('status'), value: equipment.status || '', className: "form-select mb-4 w-full", disabled: disableStatusDropdown(equipment), style: {
                            pointerEvents: disableStatusDropdown(equipment) ? 'none' : '',
                        } },
                        React.createElement("option", { value: "" }, t('SELECT_EQUIPMENT_STATUS')),
                        React.createElement("option", { value: "unavailable", key: 0 }, t('UNAVAILABLE', { count: 1 })),
                        React.createElement("option", { value: "available", key: 1 }, t('AVAILABLE', { count: 1 })),
                        React.createElement("option", { value: "closed_area", key: 2 }, t('CLOSED_AREA')),
                        React.createElement("option", { value: "on_hold", key: 3 }, t('ON_HOLD')))))));
    }
    renderEquipmentType() {
        const { equipment, t } = this.props;
        const disableEquipmentType = (equipment === null || equipment === void 0 ? void 0 : equipment._equipmentType) !== equipmentTypesMapping.CONTAINER;
        return (React.createElement("div", { className: classnames(this.getValidationClassName('_type')) },
            React.createElement("select", { onChange: this.handleChange('_type'), value: equipment._type, className: "form-select mb-4 w-full", disabled: disableEquipmentType },
                React.createElement("option", { value: "" }, t('SELECT_EQUIPMENT_TYPE')),
                _.map(equipmentTypes, (label, key) => (React.createElement("option", { key: key, value: key }, t(sluggify(label))))))));
    }
    renderToggleSwitch() {
        const { equipment, t } = this.props;
        const disableIsPrivate = (equipment === null || equipment === void 0 ? void 0 : equipment._equipmentType) !== equipmentTypesMapping.CHASSIS;
        return (React.createElement("div", null,
            React.createElement(ToggleSwitch, { enabled: !!(equipment === null || equipment === void 0 ? void 0 : equipment.is_private), disabled: disableIsPrivate, onChange: this.handleToggleChange('is_private'), "data-testid": "bulk-equipment-is-private-switch" })));
    }
    renderChassisOwnerType() {
        const { equipment, t } = this.props;
        return (React.createElement(CargomaticSelect, { selected: equipment === null || equipment === void 0 ? void 0 : equipment.chassis_owner_type, className: "mx-1.5 w-full", disabled: !(equipment === null || equipment === void 0 ? void 0 : equipment.is_private), onSelect: ({ value }) => {
                this.props.onFieldChange(this.props.equipment._id, 'chassis_owner_type', value);
            }, hideLabel: true, defaultSelectLabel: "", items: CHASSIS_OWNERSHIP_TYPE, textKey: "localizationSlug", valueKey: "value", "data-testid": "bulk-equipment-owner-type-dropdown" }));
    }
    renderCustomerSearch() {
        const { equipment, t } = this.props;
        return (React.createElement(CustomerSearch, { id: "equipment-customer-search", clearOnSelect: true, assignField: true, disabled: (equipment === null || equipment === void 0 ? void 0 : equipment.chassis_owner_type) !== 'customer', onChange: (_, chassis_owner) => {
                this.props.onFieldChange(this.props.equipment._id, 'chassis_owner', chassis_owner);
            }, onSelect: (_, chassis_owner) => {
                this.props.onFieldChange(this.props.equipment._id, 'chassis_owner', chassis_owner);
            }, value: getCustomerName(equipment === null || equipment === void 0 ? void 0 : equipment.chassis_owner), placeholder: t('SEARCH_FOR_CUSTOMER'), type: "customer", "data-testid": "bulk-equipment-customer-search" }));
    }
    renderEquipmentSizes() {
        const { equipment, t } = this.props;
        const disableEquipmentSize = (equipment === null || equipment === void 0 ? void 0 : equipment._equipmentType) !== equipmentTypesMapping.CONTAINER;
        return (React.createElement("div", { className: classnames(this.getValidationClassName('_equipmentSize')) },
            React.createElement("select", { onChange: this.handleChange('_equipmentSize'), value: equipment._equipmentSize || '', className: "form-select mb-4 w-full", disabled: disableEquipmentSize },
                React.createElement("option", { value: "" }, t('SELECT_EQUIPMENT_SIZE')),
                equipmentSizes.map((size) => (React.createElement("option", { key: size, value: size }, size))))));
    }
    renderColumns() {
        const { equipment } = this.props;
        const { _id: id } = equipment;
        const idKey = `id_${id}`;
        const assetTypeKey = `asset_type_${id}`;
        const statusKey = `status_${id}`;
        const typeKey = `type_${id}`;
        const sizeKey = `size_${id}`;
        const toggleKey = `toggle_${id}`;
        const chassisOwnerTypeKey = `chassis_owner_type_${id}`;
        const customerKey = `chassis_owner_${id}`;
        const applyAllKey = `apply_${id}`;
        const removeKey = `remove_${id}`;
        return [
            React.createElement("td", { key: idKey, className: "p-2" }, this.renderEquipmentIdField()),
            React.createElement("td", { key: assetTypeKey, className: "p-2" }, this.renderAssetTypeField()),
            React.createElement("td", { key: statusKey, className: "p-2" }, this.renderStatuses()),
            React.createElement("td", { key: typeKey, className: "p-2" }, this.renderEquipmentType()),
            React.createElement("td", { key: sizeKey, className: "p-2" }, this.renderEquipmentSizes()),
            React.createElement("td", { key: toggleKey, className: "p-2" }, this.renderToggleSwitch()),
            React.createElement("td", { key: chassisOwnerTypeKey, className: "p-2" }, this.renderChassisOwnerType()),
            React.createElement("td", { key: customerKey, className: "p-2" }, this.renderCustomerSearch()),
            React.createElement("td", { key: applyAllKey, className: "p-2" }, this.renderApplyToAllButton()),
            React.createElement("td", { key: removeKey, className: "p-2" }, this.renderRemoveActionColumn()),
        ];
    }
    renderApplyToAllButton() {
        const buttonClassName = {
            button: true,
            'button-hollow-green': true,
        };
        const { actionsDisabled, t } = this.props;
        const disabled = actionsDisabled;
        return (React.createElement("button", { type: "button", className: classnames(buttonClassName), onClick: this.handleApplyToAll, disabled: disabled }, titleize(t('APPLY'))));
    }
    renderRemoveActionColumn() {
        return (React.createElement("button", { className: "button button-secondary", onClick: this.handleDeselect, disabled: this.props.actionsDisabled },
            React.createElement("i", { className: "fa fa-remove" })));
    }
    render() {
        return React.createElement("tr", null, this.renderColumns());
    }
}
EquipmentFormRow.propTypes = {
    equipment: PropTypes.object.isRequired,
    actionsDisabled: PropTypes.bool.isRequired,
    onDeselect: PropTypes.func.isRequired,
    onApplyToAll: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
};
export default withTranslation()(EquipmentFormRow);
